import { Component, computed, effect, HostBinding, inject, input, signal } from '@angular/core';
import { CompanyUsersService } from '../../../core';
import { getInitials } from '../../utils/get-initials.util';
import { resolveHttpOrRelativeApiUrl } from '../../utils/resolve-api-asset-url.util';

/** Rótulo só traços / espaços / “sem pessoa” — não usar getInitials (evita glifo fino e oval visual). */
function isPlaceholderPersonLabel(raw: string): boolean {
  const t = raw.trim();
  if (!t) return true;
  return /^[\s\u00ad\u3000·\u2022\u2212\u002d\u2010\u2011\u2013\u2014\u2015\uFE58\uFE63\uFF0D–—]+$/u.test(
    t
  );
}

@Component({
  selector: 'app-user-avatar',
  standalone: true,
  host: {
    class: 'user-avatar-host',
  },
  template: `
    @if (photoUrl(); as src) {
      <span
        class="ua"
        [class.ua--img]="true"
        [style.--ua-size.px]="size()"
        [attr.title]="tooltip()"
        [attr.aria-label]="tooltip()"
      >
        <img [src]="src" [alt]="''" class="ua__img" loading="lazy" (error)="onPhotoError()" />
      </span>
    } @else {
      <span
        class="ua ua--initials"
        [class.ua--single-glyph]="initials().length <= 1"
        [style.--ua-size.px]="size()"
        [attr.title]="tooltip()"
        [attr.aria-label]="tooltip()"
      >
        {{ initials() }}
      </span>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-shrink: 0;
        flex-grow: 0;
        align-items: center;
        justify-content: center;
        vertical-align: middle;
        line-height: 0;
        box-sizing: border-box;
        aspect-ratio: 1;
        /* Tom mais escuro que o lavanda muito claro; uniforme para todos sem foto. */
        --ua-initials-bg: #c7d2fe;
        --ua-initials-fg: #4338ca;
      }

      :host-context(html[data-theme='dark']),
      :host-context(html.dark) {
        --ua-initials-bg: #312e81;
        --ua-initials-fg: #e0e7ff;
      }

      .ua {
        --ua-size: 28;
        box-sizing: border-box;
        /* Preenche o :host (tamanho fixo por binding) — evita círculo oval por subpixel / flex. */
        width: 100%;
        height: 100%;
        min-width: 100%;
        max-width: 100%;
        min-height: 100%;
        max-height: 100%;
        aspect-ratio: 1;
        border-radius: 50%;
        border: none;
        outline: none;
        box-shadow: none;
        font-size: calc(var(--ua-size) * 0.385px);
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        flex-shrink: 0;
        line-height: 1;
        overflow: hidden;
        letter-spacing: -0.04em;
        text-transform: uppercase;
        font-variant-numeric: tabular-nums;
        -webkit-font-smoothing: antialiased;
      }

      .ua--initials {
        background: var(--ua-initials-bg);
        color: var(--ua-initials-fg);
      }

      .ua--initials.ua--single-glyph {
        font-size: calc(var(--ua-size) * 0.48px);
        letter-spacing: 0;
      }

      .ua--img {
        padding: 0;
        background: var(--bg-surface);
      }

      .ua__img {
        width: 100%;
        height: 100%;
        min-width: 100%;
        min-height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
        border-radius: 50%;
      }
    `,
  ],
})
export class UserAvatarComponent {
  private static readonly failedPhotoSrcCache = new Set<string>();
  private readonly companyUsers = inject(CompanyUsersService);

  /** Quando definido, tenta foto e cor estável; senão usa só o label. */
  readonly userId = input<string | null | undefined>(null);
  /** Nome exibido / fallback para iniciais. */
  readonly label = input<string>('');
  readonly size = input<number>(28);

  @HostBinding('style.width.px')
  get hostWidthPx(): number {
    return this.size();
  }

  @HostBinding('style.height.px')
  get hostHeightPx(): number {
    return this.size();
  }

  @HostBinding('style.minWidth.px')
  get hostMinWidthPx(): number {
    return this.size();
  }

  @HostBinding('style.minHeight.px')
  get hostMinHeightPx(): number {
    return this.size();
  }

  @HostBinding('style.maxWidth.px')
  get hostMaxWidthPx(): number {
    return this.size();
  }

  @HostBinding('style.maxHeight.px')
  get hostMaxHeightPx(): number {
    return this.size();
  }
  /** Sobrescreve URL da foto (ex.: uso futuro). */
  readonly photoOverride = input<string | null | undefined>(null);

  private readonly resolvedName = computed(() => {
    const id = (this.userId() ?? '').trim();
    const lbl = (this.label() ?? '').trim();
    if (id) {
      const fromDir = this.companyUsers.resolveUserDisplayName(id).trim();
      if (fromDir && fromDir !== id) return fromDir;
    }
    return lbl || (id ? id : '');
  });

  readonly tooltip = computed(() => this.resolvedName() || undefined);
  private readonly photoFailed = signal(false);

  readonly initials = computed(() => {
    const name = this.resolvedName().trim();
    if (isPlaceholderPersonLabel(name)) {
      return '\u2212';
    }
    return getInitials(name, '?');
  });

  private readonly resolvedPhotoUrl = computed(() => {
    const over = (this.photoOverride() ?? '').trim();
    const id = (this.userId() ?? '').trim();
    const raw = over || (id ? (this.companyUsers.resolveUserAvatarUrl(id) ?? '') : '');
    const t = raw.trim();
    if (!t) return null;
    if (t.startsWith('blob:') || t.startsWith('data:')) return t;
    const resolved = resolveHttpOrRelativeApiUrl(t) || t;
    return resolved.trim() || null;
  });

  readonly photoUrl = computed(() => {
    if (this.photoFailed()) return null;
    const resolved = this.resolvedPhotoUrl();
    if (!resolved) return null;
    return UserAvatarComponent.failedPhotoSrcCache.has(resolved) ? null : resolved;
  });

  constructor() {
    effect(() => {
      void this.resolvedPhotoUrl();
      this.photoFailed.set(false);
    });
  }

  onPhotoError(): void {
    const resolved = this.resolvedPhotoUrl();
    if (resolved) {
      UserAvatarComponent.failedPhotoSrcCache.add(resolved);
    }
    this.photoFailed.set(true);
  }
}
